/**
 * หน้าจอความล้มเหลวของใบสั่งผลิต — หนึ่งสถานะ หนึ่งข้อความ
 *
 * ทุกกรณีที่ `docs/API_CONTRACT.md` นิยามไว้ต้องอ่านออกว่าเกิดอะไรและต้องทำอะไรต่อ
 * **ห้ามยุบเป็น "ทำรายการไม่สำเร็จ" ก้อนเดียว** — ผู้ใช้ที่ไม่รู้ว่าชนกฎข้อไหนจะแก้
 * อะไรไม่ถูก แล้วสรุปว่าระบบเสีย
 *
 * สองข้อที่ contract เขียนไว้ตรง ๆ และไฟล์นี้มีอยู่เพื่อรักษา:
 *
 * 1. **`NO_ACTIVE_BOM` กับ `NO_ACTIVE_ROUTING` ต้องเป็นคนละข้อความ** และต้องบอกว่า
 *    ขาดอะไร เพราะวิศวกรต้องรู้ว่าต้องไปสร้างอะไรต่อ — ข้อความรวมว่า "ยังไม่มีสูตร"
 *    จะส่งเขาไปเปิดหน้าจอที่มีของอยู่แล้ว
 * 2. **`APPROVER_MUST_DIFFER_FROM_CREATOR` ที่นี่คือความหมายที่สามของรหัสเดียวกัน**
 *    (หลังผู้สร้างสินค้า และผู้ยื่นคำขอเปลี่ยนแปลง) — ที่นี่หมายถึง **ผู้เปิดใบสั่ง
 *    ผลิตใบนี้** ถ้อยคำจึงต่างจากอีกสองหน้าจอโดยตั้งใจ ไม่ใช่เพราะเขียนคนละครั้ง
 *
 * `403` ของกลุ่มนี้ **ไม่ใช่** "ทำรายการไม่สำเร็จ" แต่คือ "โรงงานนี้อยู่นอกขอบเขต
 * ของคุณ" (D-004 — contract ระบุว่านอก scope ตอบ `403` ไม่ใช่ `404`)
 */

import { orderStatusLabel } from './productionOrderView';
import { StatusBadge } from '../StatusBadge';
import {
  currentStatusFromFailure,
  type ApiErrorEnvelope,
  type ProductionOrderFailure,
} from '../../lib/productionOrders/client';
import {
  APPROVER_MUST_DIFFER_FROM_CREATOR,
  NO_ACTIVE_BOM,
  NO_ACTIVE_ROUTING,
  PRODUCT_NOT_ACTIVE,
  SOURCE_TYPE_RESERVED_FOR_ADAPTER,
} from '../../lib/productionOrders/types';

export type ProductionOrderFailureContext =
  /** อ่านรายการใบสั่งผลิต */
  | 'list'
  /** อ่านใบสั่งผลิตหนึ่งใบ */
  | 'detail'
  /** เปิดใบสั่งผลิตใหม่ */
  | 'create'
  /** สั่งปล่อย */
  | 'release';

export interface ProductionOrderFailureNoticeProps {
  readonly failure: ProductionOrderFailure;
  readonly context: ProductionOrderFailureContext;
  /** โรงงานที่คำขอนี้อ้างถึง — `403` อ่านรู้เรื่องขึ้นมากเมื่อบอกว่าโรงงานไหน */
  readonly plantCode?: string;
  readonly onRetry?: () => void;
}

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function envelopeOf(failure: ProductionOrderFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

/**
 * ข้อความของกฎทางธุรกิจแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * แยกออกมาจาก `failureCopy()` เพราะกฎเหล่านี้มาพร้อม `422` เสมอ แต่ **ความหมายของ
 * มันไม่ใช่ "422"** มันคือคำอธิบายว่าต้องแก้อะไร และนั่นคือสิ่งเดียวที่ผู้ใช้ต้องอ่าน
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case NO_ACTIVE_BOM:
      return {
        heading: 'ยังไม่มีสูตรการผลิต (BOM) ที่ใช้อยู่ของสินค้านี้ในโรงงานนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_ACTIVE_BOM} — การสั่งปล่อยต้อง snapshot BOM ที่ใช้อยู่จริงติดไปกับใบสั่งผลิต เมื่อยังไม่มีฉบับที่ ACTIVE และยังไม่ถูกปิด ก็ไม่มีอะไรให้ snapshot ใบสั่งผลิตจึงยังเป็นร่างเหมือนเดิมทุกประการ — ไปเขียน BOM ของสินค้ากับโรงงานคู่นี้ที่หน้าจอ “BOM และ Routing” ก่อน แล้วกลับมาสั่งปล่อยอีกครั้ง (ขั้นตอนการผลิตเป็นคนละฉบับกัน และไม่ได้ถูกพูดถึงในข้อความนี้)`,
      };
    case NO_ACTIVE_ROUTING:
      return {
        heading: 'ยังไม่มีขั้นตอนการผลิต (Routing) ที่ใช้อยู่ของสินค้านี้ในโรงงานนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_ACTIVE_ROUTING} — การสั่งปล่อยจะ copy ทุกขั้นตอนของ Routing ฉบับที่ใช้อยู่เข้ามาเป็นงานของใบนี้ เมื่อยังไม่มีฉบับที่ ACTIVE ก็ไม่มีขั้นตอนให้หน้างานทำ ใบสั่งผลิตจึงยังเป็นร่างเหมือนเดิมทุกประการ — ไปเขียน Routing ของสินค้ากับโรงงานคู่นี้ก่อน สิ่งที่ขาดคือ Routing ไม่ใช่ BOM`,
      };
    case APPROVER_MUST_DIFFER_FROM_CREATOR:
      // ⚠️ ความหมายที่สามของรหัสเดียวกัน — ที่นี่คือผู้เปิดใบสั่งผลิต **ใบนี้**
      // ไม่ใช่ผู้สร้างสินค้า และไม่ใช่ผู้ยื่นคำขอเปลี่ยนแปลง ถ้อยคำจึงต้องไม่ซ้ำกับ
      // สองหน้าจอนั้น ไม่งั้นผู้อนุมัติจะไปตามหาคนผิดคน
      return {
        heading: 'คุณเป็นผู้เปิดใบสั่งผลิตใบนี้เอง จึงสั่งปล่อยใบนี้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${APPROVER_MUST_DIFFER_FROM_CREATOR} — การสั่งปล่อยบันทึก released_by_employee_id ซึ่งห้ามเป็นคนเดียวกับ created_by_employee_id ของใบสั่งผลิตใบนี้ (D-010) บัญชีนี้มีสิทธิ์ APPROVE ของใบสั่งผลิตจริง จึงไม่ใช่ 403 — สิ่งที่ติดคือคนละคนกันไม่ได้กับใบที่ตัวเองเปิด ต้องให้ผู้มีสิทธิ์สั่งปล่อยอีกคนเป็นผู้กดปล่อยใบนี้ ใบยังเป็นร่างและไม่มีการ snapshot สูตรเกิดขึ้น`,
      };
    case PRODUCT_NOT_ACTIVE:
      return {
        heading: 'สินค้าตัวนี้ยังใช้งานจริงในโรงงานนี้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_ACTIVE} — เปิดใบสั่งผลิตได้เฉพาะสินค้าที่สถานะเป็น ACTIVE และมีข้อมูลระดับโรงงาน (product_plant_data) ของโรงงานที่เลือกแล้ว ตรวจว่าเดินเส้นทางอนุมัติสินค้าจนจบหรือยัง และเลือกโรงงานถูกแห่งหรือไม่ — ยังไม่มีใบสั่งผลิตถูกสร้าง`,
      };
    case SOURCE_TYPE_RESERVED_FOR_ADAPTER:
      return {
        heading: 'ที่มาของใบสั่งผลิตเป็นของเซิร์ฟเวอร์ ไม่ใช่ของหน้าจอ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SOURCE_TYPE_RESERVED_FOR_ADAPTER} — Phase 1 บังคับ source_type เป็น MANUAL เสมอ ส่วน MPS/MRP/KANBAN สงวนไว้ให้ source adapter ในอนาคต (D-034) หน้าจอนี้ไม่มีตัวเลือกที่มาให้เลือกและไม่ส่งฟิลด์นี้ไปด้วย — ถ้าเห็นข้อความนี้แปลว่ามีอย่างอื่นส่งค่าเข้ามา ให้แจ้งผู้ดูแลระบบ`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: ProductionOrderFailure,
  context: ProductionOrderFailureContext,
  plantCode: string | undefined,
): FailureCopy {
  const plant = plantCode === undefined || plantCode === '' ? 'โรงงานที่เลือกอยู่' : plantCode;
  // ความล้มเหลวของคำสั่งไม่เคยขยับสถานะบนจอ — ประโยคนี้ต้องอยู่กับทุกกรณีของ
  // `release`/`create` ไม่ใช่เฉพาะกรณีที่นึกออก
  const unchanged =
    context === 'release'
      ? ' สถานะที่แสดงอยู่บนหน้าจอยังเป็นสถานะเดิม เพราะความล้มเหลวไม่ใช่การเปลี่ยนสถานะ'
      : '';

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      // 403 ของกลุ่มนี้แปลว่า "อยู่นอกขอบเขตโรงงานของคุณ" เป็นอันดับแรก เพราะ
      // `plant_code` บังคับเสมอและเป็นขอบเขตสิทธิ์ (D-004) — contract ระบุด้วยว่า
      // Plant นอก scope ตอบ `403` ไม่ใช่ `404` โดยตั้งใจ
      return {
        heading: `โรงงาน ${plant} อยู่นอกขอบเขตที่คุณเข้าถึงได้`,
        text: `เซิร์ฟเวอร์ตอบ 403 — ใบสั่งผลิตเป็นของโรงงานใดโรงงานหนึ่งเสมอ และบัญชีนี้เข้าถึงโรงงานนั้นไม่ได้ (D-004) ไม่ใช่ว่าไม่มีข้อมูล — เลือกโรงงานอื่นที่คุณมีสิทธิ์ หรือขอสิทธิ์โรงงานนี้กับผู้ดูแลระบบ${unchanged}`,
      };
    case 'not-found':
      return {
        heading:
          context === 'detail'
            ? 'ไม่พบใบสั่งผลิตใบนี้ในมุมมองของคุณ'
            : 'ปลายทางของคำขอนี้ไม่มีอยู่',
        text:
          context === 'release'
            ? `เซิร์ฟเวอร์ตอบ 404 — ใบสั่งผลิตใบนี้ถูกลบหรืออยู่นอกมุมมองของคุณไปแล้วระหว่างที่หน้าจอเปิดค้าง เปิดหน้ารายการใหม่เพื่อดูสถานะจริง${unchanged}`
            : 'เซิร์ฟเวอร์ตอบ 404 — ระบบไม่ยืนยันว่าใบนี้ไม่มีอยู่จริง หรือมีอยู่แต่อยู่นอกโรงงานที่คุณมีสิทธิ์เห็น สองอย่างนี้ตอบเหมือนกันโดยตั้งใจ',
      };
    case 'conflict': {
      // `409` ตอบสถานะจริงมาด้วยเสมอตาม contract — นั่นคือสิ่งเดียวที่บอกผู้ใช้ได้
      // ว่าหน้าจอที่เปิดค้างไว้ตกยุคไปเป็นอะไร
      const current = currentStatusFromFailure(failure);
      return {
        heading: 'มีคนขยับใบสั่งผลิตใบนี้ไปแล้ว',
        text:
          current === null
            ? `เซิร์ฟเวอร์ตอบ 409 — สถานะที่หน้าจอส่งไปกับคำสั่ง (expected_status) ไม่ตรงกับสถานะจริงของใบนี้แล้ว คำสั่งจึงไม่ถูกดำเนินการ และไม่มีการ snapshot สูตรเกิดขึ้น${unchanged}`
            : `เซิร์ฟเวอร์ตอบ 409 — สถานะจริงของใบนี้ตอนนี้คือ ${orderStatusLabel(current)} (${current}) ซึ่งไม่ตรงกับสถานะที่หน้าจอส่งไปกับคำสั่ง คำสั่งจึงไม่ถูกดำเนินการ และไม่มีการ snapshot สูตรเกิดขึ้น — อ่านสถานะจริงให้ครบก่อนตัดสินใจใหม่ หน้าจอจะไม่ส่งคำสั่งเดิมซ้ำให้เอง${unchanged}`,
      };
    }
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำสั่งนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน และยังไม่มีอะไรถูกบันทึก${unchanged}`,
        }
      );
    case 'bad-request':
      return {
        heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
        text:
          context === 'create'
            ? 'เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น จำนวนที่วางแผนไม่มากกว่า 0 วันที่สิ้นสุดไม่ได้อยู่หลังวันที่เริ่ม หรือมีฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของติดไปด้วย) ยังไม่มีใบสั่งผลิตถูกสร้าง'
            : `เซิร์ฟเวอร์ตอบ 400 — คำขอที่ส่งไปไม่ตรงรูปแบบที่ API รับ${unchanged}`,
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text:
          context === 'release'
            ? `คำสั่งไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ไม่มีอะไรยืนยันว่าการสั่งปล่อยเกิดขึ้นหรือไม่ — ให้ถือว่าใบนี้ยังเป็นสถานะเดิมจนกว่าจะโหลดใหม่แล้วเห็นด้วยตา และห้ามกดซ้ำก่อนตรวจ${unchanged}`
            : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text:
          context === 'list' || context === 'detail'
            ? `${failure.detail} — หน้าจอนี้จึงไม่มีใบสั่งผลิตจะแสดง และจะไม่เดาข้อมูลขึ้นมาเอง`
            : `${failure.detail} — คำขอไปไม่ถึงเซิร์ฟเวอร์ จึงยังไม่มีอะไรถูกบันทึก และหน้าจอนี้จะไม่ส่งซ้ำให้เองภายหลัง${unchanged}`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงใบสั่งผลิตที่อ่านได้ไม่ครบ ดีกว่าแสดงจำนวนหรือสถานะที่ไม่รู้ว่าถูกหรือผิด แล้วมีคนสั่งผลิตตาม',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

export function ProductionOrderFailureNotice({
  failure,
  context,
  plantCode,
  onRetry,
}: ProductionOrderFailureNoticeProps) {
  const copy = failureCopy(failure, context, plantCode);
  const envelope = envelopeOf(failure);
  const rule = envelope === null ? null : envelope.rule;
  const serverMessage = envelope === null ? null : envelope.message;
  const currentStatus = currentStatusFromFailure(failure);

  return (
    <div
      className="cap-notice"
      data-testid="production-order-failure"
      data-failure={failure.status}
      data-rule={rule ?? ''}
      data-current-status={currentStatus ?? ''}
      role="status"
    >
      <StatusBadge tone={failure.status === 'rule-violation' ? 'warning' : 'danger'}>
        {failure.status === 'rule-violation' ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="production-order-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="production-order-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
